import { useEffect } from "react";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { Route as RootRoute } from "./__root";
import { useAuthStore } from "../lib/authStore";

/**
 * `/` index route — landing/router.
 *
 * Chování:
 *   - state="loading" → render spinner placeholder.
 *   - state="anonymous" → redirect /login.
 *   - state="authenticated":
 *     - globalAdmin → /admin/dashboard
 *     - tenantAdmin / subAdmin / endUser → /tenant/dashboard
 *   - Při `?reason=forbidden_admin|forbidden_tenant` zobrazí 403 inline message
 *     (TEST-010: tenant JWT na /admin/* → guard redirectne sem s reason flag).
 */
export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/",
  validateSearch: (s: Record<string, unknown>) => ({
    reason: typeof s.reason === "string" ? s.reason : undefined,
  }),
  component: IndexComponent,
});

function IndexComponent() {
  const auth = useAuthStore((s) => s.auth);
  const search = Route.useSearch();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.state === "anonymous") {
      navigate({ to: "/login", search: { next: undefined } });
      return;
    }
    if (auth.state === "authenticated") {
      if (auth.user.requiresConsentRePrompt) {
        navigate({ to: "/consent/re-prompt" });
        return;
      }
      if (auth.user.role === "globalAdmin") {
        navigate({ to: "/admin/dashboard" });
      } else {
        navigate({ to: "/tenant/dashboard" });
      }
    }
  }, [auth, navigate]);

  return (
    <main className="mx-auto my-16 max-w-[420px] rounded-xl bg-primary p-8 shadow-md">
      <h1 className="m-0 mb-2 text-2xl font-semibold text-primary">axigon</h1>
      {search.reason === "forbidden_admin" && (
        <p role="alert" className="text-sm font-medium text-error-primary">
          Nemáte oprávnění přistoupit do admin sekce. Použijte správný účet.
        </p>
      )}
      {search.reason === "forbidden_tenant" && (
        <p role="alert" className="text-sm font-medium text-error-primary">
          Vaše role nepatří mezi tenant role. Použijte správný účet.
        </p>
      )}
      {search.reason === "forbidden_role" && (
        <p role="alert" className="text-sm font-medium text-error-primary">
          Pro tuto sekci nemáte potřebnou roli.
        </p>
      )}
      <p className="m-0 mb-6 text-tertiary">Přesměrování…</p>
    </main>
  );
}
